// src/modules/ida/views/PublicDocumentViewer.tsx
//
// What? Document viewer for published documents.
// Why? Allows viewing public documents without auth, and published docs with auth.
// How? Fetches document via public endpoint and renders based on file type.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Input,
  Stack,
  CssBaseline,
} from "@mui/joy";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getPreviewType,
  extractRelativeAssetFilenames,
  processHtmlWithAssets,
} from "../common/htmlPreview";
import { useMMDataBridge } from "../hooks/useMMDataBridge";
import { getApiBaseUrl } from "../../../config/runtimeConfig";
import { publicDocumentPasswordCheck } from "../../../services/publicPassword";
import { getSyncAuthToken as getStoredAuthToken } from "../../../utils/getSyncAuthToken";
import { buildAuthReturnUrl } from "../../../utils/authReturnTarget";

const API_BASE_URL = getApiBaseUrl();

interface PublicDocumentData {
  id: string;
  filename: string;
  content_type?: string;
}

const PublicDocumentViewer: React.FC = () => {
  // Use the wildcard splat param ("*") because document IDs contain slashes
  // (format: workspace_id/doc_hash). The `:param` syntax only matches a
  // single path segment, which breaks when %2F is decoded to / (common when
  // links are shared via Slack, email, Teams, etc.).
  const params = useParams();
  const document_id = params["*"] || undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docMeta, setDocMeta] = useState<PublicDocumentData | null>(null);
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [contentOrigin, setContentOrigin] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const contentUrlRef = useRef<string | null>(null);

  // Password gate state. `passwordRequired` flips the entire view to the
  // unlock screen. `refetchCounter` lets the unlock handler re-trigger the
  // fetch effect after stashing a gate token in sessionStorage, without
  // resorting to a ref to the inner fetchDocument closure.
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const revokeContentUrl = useCallback(() => {
    if (contentUrlRef.current) {
      URL.revokeObjectURL(contentUrlRef.current);
      contentUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!document_id) {
      setError("Invalid document URL");
      setLoading(false);
      return;
    }

    let cancelled = false;
    const redirectToAuthOnce = () => {
      const redirectAttemptKey = `ida_doc_auth_return_attempt_${document_id}`;
      if (sessionStorage.getItem(redirectAttemptKey)) {
        throw new Error(
          "Sign in did not grant access to this document. Ask the document owner to share the workspace with you.",
        );
      }
      sessionStorage.setItem(redirectAttemptKey, "1");
      window.location.replace(buildAuthReturnUrl());
    };
    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      revokeContentUrl();
      setContentOrigin(null);
      setContentUrl(null);

      try {
        const encodedDocId = encodeURIComponent(document_id);
        // Replay any stored delivery-gate token from a prior unlock so
        // repeat visits during the same session don't re-prompt for the
        // password. The token TTL is enforced server-side; an expired
        // token round-trips as the 401-with-password_required branch
        // below, which clears the cache and reprompts.
        const storedGate = sessionStorage.getItem(
          `ida_doc_gate_${document_id}`,
        );
        const baseUrl = `${API_BASE_URL}/api/public/documents/${encodedDocId}`;
        const url = storedGate
          ? `${baseUrl}?gate=${encodeURIComponent(storedGate)}`
          : baseUrl;

        // Include auth token if user is already signed in
        const authHeaders: Record<string, string> = {};
        const storedToken = getStoredAuthToken();
        if (storedToken) {
          authHeaders.Authorization = `Bearer ${storedToken}`;
        }

        const response = await fetch(url, {
          mode: "cors",
          headers: authHeaders,
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Document not found or not publicly accessible");
          }
          if (response.status === 401) {
            // Delivery-gate password challenge: BE returns 401 with
            // `password_required: true` when the doc is password-protected
            // and no valid `?gate=<token>` (or expired one) was supplied.
            // Clear any stale stored token, flip the view to the unlock
            // screen, and return without throwing.
            let body: { password_required?: boolean } = {};
            try {
              body = (await response.json()) as { password_required?: boolean };
            } catch {
              /* non-JSON 401 falls through to default handling below */
            }
            if (body.password_required) {
              sessionStorage.removeItem(`ida_doc_gate_${document_id}`);
              if (cancelled) return;
              setPasswordRequired(true);
              setLoading(false);
              return;
            }
            if (!storedToken) {
              redirectToAuthOnce();
              return;
            }
            throw new Error(`Failed to load document (${response.status})`);
          }
          if (response.status === 403) {
            // If we already have a token, the user is authenticated but lacks
            // workspace access -- don't redirect to login again (avoids loop).
            if (storedToken) {
              throw new Error(
                "You do not have access to this document. Ask the document owner to share the workspace with you.",
              );
            }
            // No valid token -- redirect through the auth flow.
            // /auth/return is inside the AuthProvider, so the user will be
            // prompted to sign in, then bounced back to this document URL.
            redirectToAuthOnce();
            return;
          }
          throw new Error(`Failed to load document (${response.status})`);
        }

        // Get filename from Content-Disposition header or response
        const contentDisposition =
          response.headers.get("Content-Disposition") || "";
        const contentType = response.headers.get("Content-Type") || "";
        let filename = "document";

        const filenameMatch = contentDisposition.match(
          /filename="?([^";\n]+)"?/i,
        );
        if (filenameMatch) {
          filename = filenameMatch[1].trim();
        }

        // Try to get metadata from a custom header if available
        const metaHeader = response.headers.get("X-Document-Filename");
        if (metaHeader) {
          filename = metaHeader;
        }

        if (cancelled) return;

        setDocMeta({ id: document_id, filename, content_type: contentType });

        const previewType = getPreviewType(filename);

        if (previewType === "html") {
          const sessionBaseUrl = `${API_BASE_URL}/api/public/documents/${encodedDocId}/content-session`;
          const sessionParams = new URLSearchParams({
            app_origin: window.location.origin,
          });
          if (storedGate) sessionParams.set("gate", storedGate);
          const sessionResponse = await fetch(
            `${sessionBaseUrl}?${sessionParams.toString()}`,
            { mode: "cors", headers: authHeaders },
          );
          if (sessionResponse.ok) {
            const session = (await sessionResponse.json()) as {
              configured?: boolean;
              url?: string;
              origin?: string;
            };
            if (session.configured && session.url && session.origin) {
              if (!cancelled) {
                setContentOrigin(session.origin);
                setContentUrl(session.url);
              }
              return;
            }
          } else if (sessionResponse.status === 401) {
            let body: { password_required?: boolean } = {};
            try {
              body = (await sessionResponse.json()) as {
                password_required?: boolean;
              };
            } catch {
              /* handled as a normal fallback response below */
            }
            if (body.password_required) {
              setPasswordRequired(true);
              return;
            }
            throw new Error(
              "Access to this document has expired. Reload and sign in again.",
            );
          } else {
            throw new Error(
              `Failed to open HTML preview (${sessionResponse.status})`,
            );
          }
          // The legacy inline path is used only until a lane has its content host.
          const blob = await response.blob();
          let html = await blob.text();
          if (cancelled) return;

          // Resolve linked assets (data files, CSS, images, etc.) when they are
          // statically discoverable. Always run the HTML processor afterward so
          // the mmData bridge is injected even for documents with no resolvable
          // assets.
          const assetFilenames = extractRelativeAssetFilenames(html);
          let assets = {};
          if (assetFilenames.length > 0) {
            try {
              const assetsBaseUrl = `${API_BASE_URL}/api/public/document-assets/${encodedDocId}`;
              const assetsUrl = storedGate
                ? `${assetsBaseUrl}?gate=${encodeURIComponent(storedGate)}`
                : assetsBaseUrl;
              const assetsResp = await fetch(assetsUrl, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders,
                },
                body: JSON.stringify({ filenames: assetFilenames }),
              });
              if (assetsResp.ok) {
                const resolved = await assetsResp.json();
                assets = resolved.assets || {};
              }
            } catch {
              // Non-fatal: preview still works without assets
            }
          }

          if (cancelled) return;
          html = processHtmlWithAssets(html, assets);
          const htmlBlob = new Blob([html], { type: "text/html" });
          const blobUrl = URL.createObjectURL(htmlBlob);
          contentUrlRef.current = blobUrl;
          setContentUrl(blobUrl);
        } else if (previewType === "text" || previewType === "markdown") {
          const blob = await response.blob();
          const text = await blob.text();
          setTextContent(text);
        } else if (previewType === "pdf" || previewType === "image") {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          contentUrlRef.current = blobUrl;
          setContentUrl(blobUrl);
        } else {
          // Unsupported: offer download
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          contentUrlRef.current = blobUrl;
          setContentUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [document_id, revokeContentUrl, refetchCounter]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!document_id) return;
    setPasswordSubmitting(true);
    setPasswordError(null);
    try {
      const { token } = await publicDocumentPasswordCheck(
        document_id,
        passwordInput,
      );
      sessionStorage.setItem(`ida_doc_gate_${document_id}`, token);
      setPasswordRequired(false);
      setPasswordInput("");
      // Bump the refetch counter to re-trigger the fetch effect with the
      // newly-stored gate token applied to the URL.
      setRefetchCounter((n) => n + 1);
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      const retryAfter = (err as Error & { retryAfter?: number }).retryAfter;
      if (status === 429) {
        setPasswordError(
          retryAfter !== undefined
            ? `Too many attempts. Try again in ${retryAfter}s.`
            : "Too many attempts. Try again later.",
        );
      } else if (status === 401) {
        setPasswordError("Incorrect password.");
      } else if (status === 503) {
        setPasswordError(
          "Password verification is temporarily unavailable. Please try again later.",
        );
      } else {
        setPasswordError(
          err instanceof Error ? err.message : "Password check failed",
        );
      }
    } finally {
      setPasswordSubmitting(false);
    }
  }, [document_id, passwordInput]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      revokeContentUrl();
    };
  }, [revokeContentUrl]);

  // mmData bridge: relay data mutations from iframe to backend
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = getStoredAuthToken();
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, []);
  const iframeRef = useMMDataBridge({
    documentId: document_id,
    contentOrigin,
    getAuthHeaders,
  });

  const previewType = docMeta ? getPreviewType(docMeta.filename) : null;

  if (passwordRequired) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", p: 4 }}>
        <Stack spacing={2}>
          <Typography level="h4">Password required</Typography>
          <Typography level="body-md">
            This document is password protected. Enter the password the sender
            shared with you.
          </Typography>
          <Input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePasswordSubmit();
            }}
            autoFocus
            slotProps={{ input: { autoComplete: "off" } }}
          />
          {passwordError && (
            <Alert color="danger" size="sm">
              {passwordError}
            </Alert>
          )}
          <Button
            onClick={handlePasswordSubmit}
            disabled={passwordSubmitting || !passwordInput}
          >
            {passwordSubmitting ? <CircularProgress size="sm" /> : "Unlock"}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          Loading document...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          p: 3,
        }}
      >
        <Alert color="danger" variant="soft" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // For HTML documents, render as full-page iframe
  if (previewType === "html" && contentUrl) {
    return (
      <iframe
        ref={iframeRef}
        src={contentUrl}
        title={docMeta?.filename || "Document"}
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: "100%",
          height: "100vh",
          border: "none",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.surface",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CssBaseline />

      {/* Minimal header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.surface",
        }}
      >
        <Typography level="title-md">
          {docMeta?.filename || "Document"}
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {previewType === "pdf" && contentUrl && (
          <iframe
            src={contentUrl}
            title={docMeta?.filename || "Document"}
            style={{
              width: "100%",
              flex: 1,
              border: "none",
              minHeight: "calc(100vh - 60px)",
            }}
          />
        )}

        {previewType === "image" && contentUrl && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              p: 3,
            }}
          >
            <img
              src={contentUrl}
              alt={docMeta?.filename || "Document"}
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 100px)",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        {previewType === "markdown" && textContent !== null && (
          <Box
            sx={{
              flex: 1,
              p: 3,
              maxWidth: 800,
              mx: "auto",
              width: "100%",
              fontSize: "0.9rem",
              lineHeight: 1.8,
              "& p": { my: 1 },
              "& h1": { mt: 2, mb: 1, fontSize: "1.5rem", fontWeight: 600 },
              "& h2": { mt: 2, mb: 1, fontSize: "1.25rem", fontWeight: 600 },
              "& h3": { mt: 1.5, mb: 0.5, fontSize: "1.1rem", fontWeight: 600 },
              "& ul, & ol": { pl: 2, my: 1 },
              "& li": { mb: 0.5 },
              "& code": {
                bgcolor: "neutral.100",
                px: 0.5,
                py: 0.25,
                borderRadius: "sm",
                fontSize: "0.85em",
              },
              "& pre": {
                bgcolor: "neutral.100",
                p: 1.5,
                borderRadius: "sm",
                overflow: "auto",
                "& code": { bgcolor: "transparent", p: 0 },
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {textContent}
            </ReactMarkdown>
          </Box>
        )}

        {previewType === "text" && textContent !== null && (
          <Box sx={{ flex: 1, p: 3 }}>
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                fontSize: "0.85rem",
                lineHeight: 1.6,
              }}
            >
              {textContent}
            </pre>
          </Box>
        )}

        {previewType === "unsupported" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              p: 3,
            }}
          >
            <Alert color="warning" variant="soft">
              This file type cannot be previewed in the browser.
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PublicDocumentViewer;
